import React, { useState, useRef, useEffect, lazy, Suspense } from "react";
import { motion, useMotionValue, useTransform, useScroll, useSpring } from "framer-motion";
import NavigationHeader from "@/components/NavigationHeader";
import VideoShowcase from "@/components/VideoShowcase";
import Services from "@/components/Services";
import SEO from "@/components/SEO";
import landingPageMockup from "@/assets/landing-page-mockup.webp";

// ─── Lazy-load all below-fold sections ───────────────────────────────────────
const PartnerClinics = lazy(() => import("@/components/PartnerClinics"));
const RunClinicSmarter = lazy(() => import("@/components/RunClinicSmarter"));
const FeatureScroll = lazy(() => import("@/components/FeatureScroll"));
const FreeVetAssist = lazy(() => import("@/components/FreeVetAssist"));
const DeveloperTeam = lazy(() => import("../components/DeveloperTeam"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const LatestBlogs = lazy(() => import("@/components/LatestBlogs"));
const DemoSection = lazy(() => import("@/components/DemoSection"));
const VetAssistNetwork = lazy(() => import("../components/VetAssistNetwork"));
const Footer = lazy(() => import("@/components/Footer"));

function BelowFoldFallback() {
  return <div aria-hidden="true" style={{ minHeight: "280vh", width: "100%" }} />;
}

// ─── Typewriter constants ─────────────────────────────────────────────────────
const LINE1 = "Everything your Veterinary\nClinic Needs ";
const LINE2 = "Powered by\nOne Smart Software";
const FULL_TEXT = LINE1 + LINE2;
const GRADIENT_START = LINE1.length;

// ─── HeroSection ─────────────────────────────────────────────────────────────
function HeroSection() {
  const [charCount, setCharCount] = useState(0);
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    if (charCount >= FULL_TEXT.length) {
      const t = setTimeout(() => setIsDone(true), 120);
      return () => clearTimeout(t);
    }
    const speed = charCount < 30 ? 40 : charCount < 60 ? 30 : 25;
    const t = setTimeout(() => setCharCount((c) => c + 1), speed);
    return () => clearTimeout(t);
  }, [charCount]);

  const renderTitle = () => {
    const plainPart = FULL_TEXT.slice(0, Math.min(charCount, GRADIENT_START));
    const gradPart = charCount > GRADIENT_START ? FULL_TEXT.slice(GRADIENT_START, charCount) : "";
    const toJsx = (str) =>
      str.split("\n").map((seg, i, arr) => (
        <span key={i}>
          {seg}
          {i < arr.length - 1 && <br />}
        </span>
      ));
    return (
      <>
        <span style={{ color: "#1e293b" }}>{toJsx(plainPart)}</span>
        {gradPart && (
          <span
            style={{
              background: "linear-gradient(130deg, #16a34a 0%, #22c55e 40%, #3b82f6 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}>
            {toJsx(gradPart)}
          </span>
        )}
        {!isDone && (
          <span
            style={{
              display: "inline-block",
              width: "3px",
              height: "0.88em",
              background: "#22c55e",
              verticalAlign: "text-bottom",
              marginLeft: "2px",
              animation: "blink 0.7s step-end infinite",
            }}
          />
        )}
      </>
    );
  };

  const reveal = (delayMs = 0) => ({
    opacity: isDone ? 1 : 0,
    transform: isDone ? "translateY(0)" : "translateY(18px)",
    transition: `opacity 0.55s ease ${delayMs}ms, transform 0.55s ease ${delayMs}ms`,
  });

  return (
    <section className="hero-section relative flex w-full flex-col overflow-hidden">
      {/* Blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute top-[-15%] left-[-15%] z-0 h-[75%] w-[65%] rounded-full"
          style={{
            background: "radial-gradient(ellipse, rgba(41,92,172,0.08) 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute right-[-10%] bottom-[-10%] z-0 h-[55%] w-1/2 rounded-full"
          style={{
            background: "radial-gradient(ellipse, rgba(41,92,172,0.05) 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute top-[-10%] right-[-5%] z-0 h-[55%] w-1/2 rounded-full"
          style={{
            background: "radial-gradient(ellipse, rgba(95,182,67,0.06) 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute top-[-5%] left-1/2 z-0 h-[45%] w-[60%] -translate-x-1/2 rounded-full"
          style={{
            background: "radial-gradient(ellipse, rgba(72,194,169,0.04) 0%, transparent 70%)",
          }}
        />
      </div>

      {/* Text */}
      <div className="hero-text-container relative z-20 w-full shrink-0 text-center">
        <div
          style={{ ...reveal(0), marginTop: "2rem" }}
          className="group relative mb-2 inline-flex items-center gap-2 overflow-hidden rounded-full p-[1.5px]">
          <div
            className="absolute inset-[-1000%] animate-spin"
            style={{
              background:
                "conic-gradient(from 0deg, transparent 0deg, transparent 300deg, #4ade80 360deg)",
              animationDuration: "4s",
            }}
          />
          <div className="relative flex w-full items-center gap-1.5 rounded-full bg-white px-3 py-1.5 backdrop-blur-xl sm:gap-2 sm:px-5 sm:py-2 md:px-6">
            <span className="relative flex h-2 w-2 items-center justify-center sm:h-3 sm:w-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
              <span className="relative h-1.5 w-1.5 shrink-0 rounded-full bg-green-500 shadow-[0_0_8px_#22c55e]" />
            </span>
            <span className="text-[9px] font-bold tracking-widest whitespace-nowrap text-green-700 uppercase sm:text-[11px] md:text-[13px]">
              Veterinary Practice Management Software
            </span>
          </div>
        </div>

        <h1 className="mb-2 pb-0.5 text-[clamp(1.7rem,3.8vw,4.2rem)] leading-[1.06] font-extrabold tracking-[-0.02em]">
          {renderTitle()}
        </h1>

        <p
          style={reveal(100)}
          className="mx-auto max-w-[600px] text-[clamp(13px,1.1vw,17px)] leading-relaxed font-normal text-slate-600">
          Vet Assist is an easy-to-use platform that simplifies clinic admin so you can see more
          patients and communicate better.
        </p>
      </div>

      {/* Hero mockup — explicit dimensions prevent CLS */}
      <div className="hero-mockup-outer relative z-10 w-full shrink-0" style={reveal(250)}>
        <div className="hero-mockup-wrapper" style={{ margin: "0 auto" }}>
          <img
            src={landingPageMockup}
            alt="VetAssist app across devices"
            width={1100}
            height={720}
            fetchPriority="high"
            decoding="async"
            style={{
              display: "block",
              width: "100%",
              height: "auto",
              objectFit: "contain",
              objectPosition: "top center",
            }}
          />
        </div>
      </div>

      {/* Bottom fade */}
      <div
        className="pointer-events-none absolute right-0 bottom-0 left-0 z-20 h-32"
        style={{
          background:
            "linear-gradient(to top, rgba(240,247,255,1) 0%, rgba(240,247,255,0.6) 50%, transparent 100%)",
        }}
      />
    </section>
  );
}

// ─── Sticky scroll section (isolated so Framer Motion only runs when needed) ──
function StickyScrollSection({ stickyRef, slideX, isMobile }) {
  if (isMobile) {
    return (
      <section id="video-showcase" ref={stickyRef} className="relative w-full">
        <div className="w-full">
          <VideoShowcase />
        </div>
        <div className="w-full">
          <Services />
        </div>
      </section>
    );
  }

  return (
    <section id="video-showcase" ref={stickyRef} className="relative h-[500vh]">
      <div className="sticky top-0 min-h-svh w-full overflow-hidden">
        <motion.div style={{ x: slideX }} className="flex w-[200vw]">
          <div className="h-svh w-screen shrink-0">
            <VideoShowcase />
          </div>
          <div className="flex min-h-svh w-screen shrink-0 items-stretch">
            <Services />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── HomePage ─────────────────────────────────────────────────────────────────
const HomePage = () => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const stickyRef = useRef(null);

  const { scrollYProgress: stickyProgress } = useScroll({
    target: stickyRef,
    offset: ["start start", "end end"],
  });
  const rawSlideX = useTransform(stickyProgress, [0, 0.4], ["0vw", "-100vw"]);
  const slideX = useSpring(rawSlideX, { stiffness: 60, damping: 25, mass: 0.8 });

  const handleMouseMove = (e) => {
    mouseX.set(e.clientX / window.innerWidth - 0.5);
    mouseY.set(e.clientY / window.innerHeight - 0.5);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const preloadBelowFoldSections = () => {
      import("../components/VetAssistNetwork");
      import("@/components/RunClinicSmarter");
      import("@/components/FeatureScroll");
      import("@/components/FreeVetAssist");
      import("@/components/PartnerClinics");
      import("../components/DeveloperTeam");
      import("@/components/Testimonials");
      import("@/components/LatestBlogs");
      import("@/components/DemoSection");
      import("@/components/Footer");
    };

    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(preloadBelowFoldSections);
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(preloadBelowFoldSections, 250);
    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <>
      <SEO page="home" />
      <style>{`
        @keyframes fu    { from { opacity:0; transform:translateY(32px); } to { opacity:1; transform:translateY(0); } }
        @keyframes blink { 50% { opacity: 0; } }
        *, *::before, *::after { box-sizing: border-box; }
        body { font-family: 'Poppins', sans-serif; overflow-x: hidden; }
        ::-webkit-scrollbar       { width: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

        .hero-section { overflow: hidden; margin-right: 10px; }

        @media (max-width: 639px) {
          .hero-section { padding-top: 56px; }
          .hero-text-container { padding: 10px 1.25rem 6px; }
          .hero-mockup-wrapper { width: 90%; margin-top: 8px; }
        }
        @media (min-width: 640px) and (max-width: 767px) {
          .hero-section { padding-top: 56px; }
          .hero-text-container { padding: 12px 2rem 8px; }
          .hero-mockup-wrapper { width: 85%; margin-top: 10px; }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .hero-section { padding-top: 64px; height: auto; padding-bottom: 0; }
          .hero-text-container { padding: 14px 2.5rem 8px; }
          .hero-mockup-wrapper { width: 80%; max-width: 600px; margin-top: 12px; }
        }
        @media (min-width: 1024px) and (max-width: 1279px) {
          .hero-section { padding-top: 72px; height: auto; padding-bottom: 0; }
          .hero-text-container { padding: 16px 3rem 10px; }
          .hero-mockup-wrapper { width: 75%; max-width: 700px; margin-top: 14px; }
        }
        @media (min-width: 1280px) and (max-width: 1439px) {
          .hero-section { padding-top: 72px; min-height: 100vh; padding-bottom: 48px; }
          .hero-text-container { padding: 16px 4rem 10px; }
          .hero-mockup-wrapper { width: 85%; max-width: 1000px; margin-top: 14px; }
        }
        @media (min-width: 1440px) {
          .hero-section { padding-top: 72px; min-height: 100vh; padding-bottom: 48px; }
          .hero-text-container { padding: 24px 4rem 14px; }
          .hero-mockup-wrapper { width: 82%; max-width: 1100px; margin-top: 18px; }
        }
      `}</style>

      <div
        className="min-h-screen text-slate-800"
        style={{ overflowX: "clip" }}
        onMouseMove={handleMouseMove}>
        {!isVideoModalOpen && <NavigationHeader />}
        <HeroSection />

        <div className="relative z-20" style={{ overflowX: "clip" }}>
          <StickyScrollSection stickyRef={stickyRef} slideX={slideX} isMobile={isMobile} />

          {/* All below-fold sections lazy-loaded */}
          <Suspense fallback={<BelowFoldFallback />}>
            <VetAssistNetwork />
            <RunClinicSmarter />
            <FeatureScroll onModalOpenChange={setIsVideoModalOpen} />
            <FreeVetAssist />
            <PartnerClinics />
            <DeveloperTeam />
            <Testimonials onModalOpenChange={setIsVideoModalOpen} />
            <LatestBlogs />
            <DemoSection />
            <Footer />
          </Suspense>
        </div>
      </div>
    </>
  );
};

export default HomePage;
